import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAccountQualityCheck, AccountQualityResult } from '../components/AccountQualityCheck';
import { NicheFitContent } from '../components/NicheFitModal';
import redditApi from '../../../services/redditApi';


/* ── Validation steps config ─────────────────────────────────────────────── */
// Each step becomes "active" (spinning) at `startSec` and "done" at the next step's startSec.
const STEPS = [
  { key: 'fetch',    label: 'Querying Reddit API',        startSec: 0  },
  { key: 'normalize',label: 'Parsing activity & metrics', startSec: 6  },
  { key: 'checks',   label: 'Running quality checks',     startSec: 12 },
  { key: 'ai',       label: 'Getting AI analysis',        startSec: 18 },
  { key: 'finalize', label: 'Finalizing result',          startSec: 50 },
];

function ValidationSteps({ elapsed }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 4 }}>
      {STEPS.map((step, i) => {
        const nextStart = STEPS[i + 1]?.startSec ?? Infinity;
        const isDone    = elapsed >= nextStart;
        const isActive  = !isDone && elapsed >= step.startSec;
        const isPending = elapsed < step.startSec;

        let iconColor  = 'var(--rh-text-faint)';
        let labelColor = 'var(--rh-text-faint)';
        let icon;

        if (isDone) {
          iconColor  = 'var(--rh-orange)';
          labelColor = 'var(--rh-text-muted)';
          icon = (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <circle cx="8" cy="8" r="7" fill="var(--rh-orange)" fillOpacity="0.15" stroke="var(--rh-orange)" strokeWidth="1.5" />
              <polyline points="5,8.5 7,10.5 11,6" stroke="var(--rh-orange)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          );
        } else if (isActive) {
          iconColor  = 'var(--rh-orange)';
          labelColor = 'var(--rh-text)';
          icon = (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ animation: 'aqt-spin 0.8s linear infinite' }}>
              <circle cx="8" cy="8" r="6.5" stroke="var(--rh-orange)" strokeOpacity="0.2" strokeWidth="2" />
              <path d="M8 1.5 a6.5 6.5 0 0 1 6.5 6.5" stroke="var(--rh-orange)" strokeWidth="2" strokeLinecap="round" />
            </svg>
          );
        } else {
          icon = (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <circle cx="8" cy="8" r="6.5" stroke="var(--rh-border)" strokeWidth="1.5" />
            </svg>
          );
        }

        return (
          <div key={step.key} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ flexShrink: 0, color: iconColor }}>{icon}</span>
            <span style={{ fontSize: 13, color: labelColor, fontWeight: isActive ? 600 : 400, transition: 'color 0.3s' }}>
              {step.label}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/* ── Shield illustration (idle state) ───────────────────────────────────── */
function ShieldIllustration() {
  return (
    <svg width="72" height="72" viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="36" cy="36" r="36" fill="var(--rh-orange-light)" />
      <path
        d="M36 16L20 22v14c0 9.94 6.84 19.24 16 21.6C45.16 55.24 52 45.94 52 36V22L36 16z"
        fill="var(--rh-orange)"
        fillOpacity="0.15"
        stroke="var(--rh-orange)"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <polyline
        points="29,36 34,41 43,31"
        stroke="var(--rh-orange)"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/* ── Pulsing spinner used while checking ─────────────────────────────────── */
function CheckingIllustration() {
  return (
    <div style={{ position: 'relative', width: 72, height: 72 }}>
      {/* Outer pulsing ring */}
      <div style={{
        position: 'absolute', inset: 0, borderRadius: '50%',
        background: 'var(--rh-orange-light)',
        animation: 'aqt-pulse 1.6s ease-in-out infinite',
      }} />
      <div style={{
        position: 'absolute', inset: 6, borderRadius: '50%',
        background: 'var(--rh-orange-light)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="18" cy="18" r="15" stroke="var(--rh-orange)" strokeOpacity="0.2" strokeWidth="3" />
          <path d="M18 3 a15 15 0 0 1 15 15" stroke="var(--rh-orange)" strokeWidth="3" strokeLinecap="round">
            <animateTransform attributeName="transform" type="rotate" from="0 18 18" to="360 18 18" dur="0.9s" repeatCount="indefinite" />
          </path>
        </svg>
      </div>
    </div>
  );
}

/* ── Redditor picker + add panel ─────────────────────────────────────────── */
function RedditorAvatar({ name }) {
  const initials = name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();
  return (
    <div style={{
      width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
      background: 'var(--rh-orange-light)', border: '1.5px solid var(--rh-orange-border)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 11, fontWeight: 700, color: 'var(--rh-orange)',
    }}>
      {initials}
    </div>
  );
}

function AddToRedditorPanel({
  result, redditors, redditorSearch, setRedditorSearch,
  selectedRedditor, setSelectedRedditor,
  adding, addResult, onAdd, onForceAdd,
}) {
  const [open, setOpen] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const isPassed = result.status === 'passed';
  const canForce = !isPassed;
  const showList = searchFocused || redditorSearch.length > 0;

  const filtered = redditors.filter((r) =>
    r.full_name.toLowerCase().includes(redditorSearch.toLowerCase()) ||
    r.email.toLowerCase().includes(redditorSearch.toLowerCase())
  );

  // Success state
  if (addResult?.ok) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        background: 'var(--rh-green-bg)', border: '1px solid #bbf7d0',
        borderRadius: 12, padding: '14px 18px', marginTop: 4,
      }}>
        <div style={{
          width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
          background: '#166534', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#166534' }}>Account added successfully</div>
          <div style={{ fontSize: 12, color: '#15803d', marginTop: 2 }}>{addResult.message}</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      border: '1px solid var(--rh-border)', borderRadius: 14,
      overflow: 'hidden', marginTop: 4,
      boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    }}>
      {/* Collapsible header */}
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 10,
          padding: '14px 18px', background: open ? '#fff7f5' : '#fafafa',
          border: 'none', cursor: 'pointer', textAlign: 'left',
          borderBottom: open ? '1px solid var(--rh-border)' : 'none',
          transition: 'background 0.15s',
        }}
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--rh-orange)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="8.5" cy="7" r="4" />
          <line x1="20" y1="8" x2="20" y2="14" /><line x1="23" y1="11" x2="17" y2="11" />
        </svg>
        <span style={{ flex: 1, fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--rh-text-muted)' }}>
          Assign to Redditor
        </span>
        {selectedRedditor && !open && (
          <span style={{ fontSize: 12, color: 'var(--rh-orange)', fontWeight: 600, textTransform: 'none', letterSpacing: 0 }}>
            {selectedRedditor.full_name}
          </span>
        )}
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--rh-text-faint)" strokeWidth="2.5"
          style={{ flexShrink: 0, transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'none' }}>
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {/* Collapsible body */}
      {open && <>
      {/* Search bar */}
      <div style={{ padding: '12px 18px', borderBottom: '1px solid var(--rh-border)', background: '#fff' }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          border: '1.5px solid var(--rh-border)', borderRadius: 10,
          padding: '7px 12px', background: '#f9fafb',
          transition: 'border-color 0.15s',
        }}
          onFocusCapture={(e) => e.currentTarget.style.borderColor = 'var(--rh-orange)'}
          onBlurCapture={(e) => e.currentTarget.style.borderColor = 'var(--rh-border)'}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--rh-text-faint)" strokeWidth="2" strokeLinecap="round">
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            placeholder={searchFocused ? 'Search by name or email…' : 'Click to select a redditor…'}
            value={redditorSearch}
            onChange={(e) => { setRedditorSearch(e.target.value); setSelectedRedditor(null); }}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setTimeout(() => setSearchFocused(false), 150)}
            style={{
              flex: 1, border: 'none', outline: 'none', background: 'transparent',
              fontSize: 13, color: 'var(--rh-text)',
            }}
          />
          {redditorSearch && (
            <button onClick={() => { setRedditorSearch(''); setSelectedRedditor(null); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--rh-text-faint)', lineHeight: 1, padding: 0, fontSize: 14 }}>
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Redditor list — visible only when input is focused or has text */}
      {showList && <div style={{ maxHeight: 220, overflowY: 'auto', background: '#fff' }}>
        {filtered.length === 0 ? (
          <div style={{ padding: '20px 18px', textAlign: 'center', fontSize: 13, color: 'var(--rh-text-faint)' }}>
            No redditors found.
          </div>
        ) : filtered.map((r, i) => {
          const isSelected = selectedRedditor?.id === r.id;
          return (
            <button
              key={r.id}
              onClick={() => setSelectedRedditor(isSelected ? null : r)}
              style={{
                width: '100%', textAlign: 'left', border: 'none',
                borderTop: i === 0 ? 'none' : '1px solid var(--rh-border)',
                background: isSelected ? 'var(--rh-orange-light)' : 'transparent',
                padding: '10px 18px', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 12,
                transition: 'background 0.12s',
              }}
              onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = '#f9fafb'; }}
              onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
            >
              <RedditorAvatar name={r.full_name} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: isSelected ? 'var(--rh-orange)' : 'var(--rh-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {r.full_name}
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--rh-text-faint)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {r.email}
                </div>
              </div>
              {isSelected && (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--rh-orange)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </button>
          );
        })}
      </div>}

      {/* Footer — error + action button */}
      <div style={{ padding: '12px 18px', borderTop: '1px solid var(--rh-border)', background: '#fafafa', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {addResult && !addResult.ok && (
          <div className="rh-auth-error">{addResult.message}</div>
        )}
        <div style={{ display: 'flex', gap: 8 }}>
          {isPassed && (
            <button
              className="rh-btn-primary"
              disabled={!selectedRedditor || adding}
              onClick={onAdd}
              style={{ flex: 1 }}
            >
              {adding ? 'Adding…' : selectedRedditor ? `Add to ${selectedRedditor.full_name.split(' ')[0]}` : 'Select a Redditor'}
            </button>
          )}
          {canForce && (
            <button
              className="rh-btn-force"
              disabled={!selectedRedditor || adding}
              onClick={onForceAdd}
              style={{ flex: 1 }}
            >
              {adding ? 'Adding…' : selectedRedditor ? `Force Add to ${selectedRedditor.full_name.split(' ')[0]}` : 'Select a Redditor'}
            </button>
          )}
        </div>
      </div>
      </>}
    </div>
  );
}

/* ── Main tab ────────────────────────────────────────────────────────────── */
export default function AccountQualityTab() {
  const [input, setInput] = useState('');
  const { phase, result, error, validate, reset } = useAccountQualityCheck();
  const inputRef = useRef(null);
  const [elapsed, setElapsed] = useState(0);
  const elapsedRef = useRef(null);

  // Redditor picker state
  const [redditors, setRedditors] = useState([]);
  const [redditorSearch, setRedditorSearch] = useState('');
  const [selectedRedditor, setSelectedRedditor] = useState(null);
  const [adding, setAdding] = useState(false);
  const [addResult, setAddResult] = useState(null); // { ok: bool, message: string }

  const isIdle = phase === 'idle';
  const isChecking = phase === 'checking';
  const isDone = phase === 'done';

  // Load redditor list once (non-admin members only)
  useEffect(() => {
    redditApi.get('/admin/redditors/').then(({ data }) => {
      setRedditors((data.results ?? data).filter((r) => !r.is_admin && !r.is_accountant));
    }).catch(() => {});
  }, []);

  // Tick elapsed seconds while checking
  useEffect(() => {
    if (isChecking) {
      setElapsed(0);
      elapsedRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
    } else {
      clearInterval(elapsedRef.current);
    }
    return () => clearInterval(elapsedRef.current);
  }, [isChecking]);

  // Reset add state whenever a new check starts
  useEffect(() => {
    if (isChecking) {
      setSelectedRedditor(null);
      setRedditorSearch('');
      setAddResult(null);
    }
  }, [isChecking]);

  function handleSubmit(e) {
    e.preventDefault();
    const username = input.trim().replace(/^u\//i, '');
    if (!username) return;
    validate(username);
  }

  function handleCheckAnother() {
    reset();
    setInput('');
    setElapsed(0);
    setSelectedRedditor(null);
    setRedditorSearch('');
    setAddResult(null);
    setTimeout(() => inputRef.current?.focus(), 50);
  }

  const handleAddToRedditor = useCallback(async (force = false) => {
    if (!selectedRedditor || !result?.validation_id) return;
    setAdding(true);
    setAddResult(null);
    try {
      await redditApi.post(`/admin/redditors/${selectedRedditor.id}/accounts/`, {
        validation_id: result.validation_id,
        force,
      });
      setAddResult({ ok: true, message: `u/${result.username} added to ${selectedRedditor.full_name}.` });
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to add account. Please try again.';
      setAddResult({ ok: false, message: detail });
    } finally {
      setAdding(false);
    }
  }, [selectedRedditor, result]);

  return (
    <>
      {/* Inline keyframe for the pulse animation */}
      <style>{`
        @keyframes aqt-pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50%       { transform: scale(1.15); opacity: 0.6; }
        }
        @keyframes aqt-spin { to { transform: rotate(360deg); } }
      `}</style>

      <div style={{ maxWidth: 900, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 20 }}>

        {/* ── Hero / search card ── */}
        <div style={{
          background: '#fff',
          border: '1px solid var(--rh-border)',
          borderRadius: 16,
          overflow: 'hidden',
          boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        }}>

          {/* Top stripe */}
          <div style={{
            background: 'linear-gradient(135deg, var(--rh-orange-light) 0%, #fff 100%)',
            borderBottom: '1px solid var(--rh-border)',
            padding: '32px 36px 28px',
            display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 16,
          }}>
            {isChecking ? <CheckingIllustration /> : <ShieldIllustration />}

            <div style={{ width: '100%', maxWidth: 440 }}>
              <h2 style={{ margin: '0 0 6px', fontSize: 20, fontWeight: 700, color: 'var(--rh-text)' }}>
                {isChecking
                  ? `Checking u/${result?.username ?? input.replace(/^u\//i, '')}…`
                  : 'Account Quality Check'}
              </h2>
              <p style={{ margin: 0, fontSize: 13.5, color: 'var(--rh-text-muted)' }}>
                {isChecking
                  ? 'Running a full quality check — this usually takes 20–40 seconds.'
                  : 'Vet any Reddit account independently — no user assignment required. Results are informational only.'}
              </p>
              {isChecking && (
                <div style={{ marginTop: 20, textAlign: 'left' }}>
                  <ValidationSteps elapsed={elapsed} />
                </div>
              )}
            </div>
          </div>

          {/* Search bar */}
          <div style={{ padding: '24px 36px' }}>
            <form onSubmit={handleSubmit}>
              <div style={{
                display: 'flex', gap: 10,
                background: 'var(--rh-orange-light)',
                border: '1.5px solid var(--rh-orange-border)',
                borderRadius: 12, padding: '6px 6px 6px 16px',
                transition: 'border-color 0.2s, box-shadow 0.2s',
              }}
                onFocusCapture={(e) => e.currentTarget.style.borderColor = 'var(--rh-orange)'}
                onBlurCapture={(e) => e.currentTarget.style.borderColor = 'var(--rh-orange-border)'}
              >
                {/* Reddit "u/" prefix chip */}
                <span style={{
                  alignSelf: 'center', fontSize: 13, fontWeight: 700,
                  color: 'var(--rh-orange)', userSelect: 'none', flexShrink: 0,
                }}>u/</span>
                <input
                  ref={inputRef}
                  value={input.replace(/^u\//i, '')}
                  onChange={(e) => setInput(e.target.value)}
                  disabled={isChecking}
                  placeholder="username"
                  autoComplete="off"
                  autoFocus
                  style={{
                    flex: 1, border: 'none', outline: 'none', background: 'transparent',
                    fontSize: 14, fontWeight: 500, color: 'var(--rh-text)',
                    minWidth: 0,
                  }}
                />
                <button
                  type="submit"
                  disabled={isChecking || !input.trim()}
                  className="rh-btn-primary"
                  style={{ flexShrink: 0, borderRadius: 8, padding: '9px 20px' }}
                >
                  {isChecking ? 'Checking…' : isDone ? 'Run Again' : 'Run Check'}
                </button>
              </div>
              {error && (
                <div className="rh-auth-error" style={{ marginTop: 10 }}>{error}</div>
              )}
            </form>

            {/* Idle hint bullets */}
            {isIdle && !error && (
              <div style={{ display: 'flex', gap: 24, marginTop: 18, justifyContent: 'center', flexWrap: 'wrap' }}>
                {[
                  { icon: '✓', label: 'Email verified' },
                  { icon: '✓', label: 'Account age & karma' },
                  { icon: '✓', label: 'Activity quality' },
                  { icon: '✓', label: 'Niche classification' },
                ].map(({ icon, label }) => (
                  <span key={label} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--rh-text-muted)' }}>
                    <span style={{ color: 'var(--rh-orange)', fontWeight: 700 }}>{icon}</span>
                    {label}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Result panel, shown inside the card when done */}
          {isDone && result && (
            <div style={{ borderTop: '1px solid var(--rh-border)', padding: '0 36px 28px' }}>
              <div style={{ paddingTop: 24 }}>
                <AccountQualityResult
                  result={result}
                  adding={false}
                  onAddAccount={null}
                  onRetry={handleCheckAnother}
                />
              </div>

              {/* Passed → redditor picker to add directly */}
              {(result.status === 'passed' || result.status === 'failed' || result.status === 'needs_manual_review') && (
                <AddToRedditorPanel
                  result={result}
                  redditors={redditors}
                  redditorSearch={redditorSearch}
                  setRedditorSearch={setRedditorSearch}
                  selectedRedditor={selectedRedditor}
                  setSelectedRedditor={setSelectedRedditor}
                  adding={adding}
                  addResult={addResult}
                  onAdd={() => handleAddToRedditor(false)}
                  onForceAdd={() => handleAddToRedditor(true)}
                />
              )}
            </div>
          )}
        </div>

        {/* Client Fit — separate card so screenshots of the quality report never include client names */}
        {isDone && result && (
          <div style={{
            background: '#fff',
            border: '1px solid var(--rh-border)',
            borderRadius: 16,
            overflow: 'hidden',
            boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
            padding: '24px 32px 28px',
          }}>
            <div style={{ marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--rh-text)' }}>
                Client Fit — u/{result.username}
              </h3>
              <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--rh-text-muted)' }}>
                Internal only — not part of the shareable quality report. Niches/fit do not pass or fail the quality check.
              </p>
            </div>
            <NicheFitContent
              account={{
                username: result.username,
                niches: result.niches,
                niche_fit: result.niche_fit,
              }}
              showNiches
            />
          </div>
        )}

      </div>
    </>
  );
}
